import React from "react";
import styled from "styled-components";

/**
 * DefaultAvatar - A reusable component for displaying circular default avatars
 * when profile images fail to load or are not available.
 * 
 * @param {string} name - The user's name to generate initials from
 * @param {string|number} size - Size in pixels (default: 120)
 * @param {string} className - Optional className for styling
 * @param {object} style - Optional inline styles
 * @param {string} fontSize - Optional custom font size (auto-calculated if not provided)
 * @param {string} backgroundColor - Optional background color (default: var(--color-border))
 * @param {string} color - Optional text color (default: var(--color-text))
 * @param {string} border - Optional border style (default: none)
 * @param {string} boxShadow - Optional box shadow
 */
const DefaultAvatar = ({
  name = "U",
  size = 120,
  className = "",
  style = {},
  fontSize = null,
  backgroundColor = "var(--color-border)",
  color = "var(--color-text)",
  border = "none",
  boxShadow = null,
}) => {
  // Get initials from name
  const getInitials = (nameStr) => {
    if (!nameStr || nameStr === "U") return "U";
    const words = nameStr.trim().split(" ");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return nameStr.charAt(0).toUpperCase();
  };

  // Calculate font size based on avatar size if not provided
  const calculatedFontSize = fontSize || `${Math.floor(size * 0.4)}px`;

  const initials = getInitials(name);

  return (
    <AvatarContainer
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: calculatedFontSize,
        backgroundColor,
        color,
        border,
        boxShadow,
        ...style,
      }}
    >
      {initials}
    </AvatarContainer>
  );
};

const AvatarContainer = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
  user-select: none;
`;

export default DefaultAvatar;
